/**
 * Schwachstellen — the Dependency-Track picture, keyed by ProduktInstanz.
 *
 * DT organises everything by project UUID, which means nothing to someone
 * thinking in customers and serial numbers. This page flips the axis: pick the
 * delivered machine, see its findings and its SBOM. Nobody needs the DT UI for
 * the read path any more.
 *
 * Read-only by decision (see app/services/dt_view_service.py): the applicability
 * verdict is a VulnerabilityEvent in AssetRadar, so DT never receives an analysis
 * from here and there is exactly one triage truth.
 *
 * Two axes, switched at the top: „Nach CVE“ aggregates every finding of every
 * instance into one row per CVE with the affected products under it, „Nach
 * Instanz“ is the per-machine drill-down with findings and SBOM.
 *
 * Load strategy: the instance list is one request with a cheap metrics lookup per
 * version project (instances on one version share a DT project, DEV-107).
 * Findings and SBOM are fetched only for the selected row — a project with a few
 * thousand components is not something to prefetch eleven times over.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import Banner from '../components/Banner';
import Chip, { ChipList } from '../components/Chip';
import DataTable, { type DataTableColumn } from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import Icon from '../components/Icon';
import { KpiRow, KpiTile } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import Spinner, { PageSpinner } from '../components/Spinner';
import {
  errorMessage,
  fetchDtCves,
  fetchDtFindings,
  fetchDtInstances,
  fetchDtSbom,
} from '../lib/api';
import { DASH, fmtDate, fmtDay, fmtNumber, fmtScore, fmtVersion, plural } from '../lib/format';
import type {
  DtCvesResponse,
  DtFindingsResponse,
  DtInstanceRow,
  DtInstancesResponse,
  DtSbomResponse,
  DtSeverity,
  Tone,
} from '../lib/types';

import CvePanel, { CoverageWarning, type CveFilters } from './schwachstellen/CvePanel';
import FindingsPanel from './schwachstellen/FindingsPanel';
import SbomPanel from './schwachstellen/SbomPanel';
import { SCAN_STATE_LABELS, SCAN_STATE_TONE, SEVERITY_LABELS, SEVERITY_TONE } from './schwachstellen/severity';

import './schwachstellen/schwachstellen.css';

type Tab = 'befunde' | 'sbom';
const AUTO_REFRESH_MS = 60_000;

/**
 * Which axis the page shows. 'cve' is the default because that is the question
 * a CRA report asks first — welche Schwachstelle betrifft welche Produkte;
 * 'instanz' is the per-machine drill-down.
 */
type View = 'cve' | 'instanz';
type OverviewTarget = 'instances' | 'critical' | 'high' | 'without-sbom' | 'unknown';

/** Severity chips in the list row — zero counts are omitted, not shown as 0. */
function SeverityChips({ row }: { row: DtInstanceRow }): ReactNode {
  const entries = (['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as const).filter(
    (key) => (row.severities[key] ?? 0) > 0,
  );
  if (entries.length === 0) {
    return <span className="muted">{row.scan_state === 'ok' ? 'keine' : DASH}</span>;
  }
  return (
    <ChipList>
      {entries.map((key: DtSeverity) => (
        <Chip key={key} tone={SEVERITY_TONE[key]} title={`${row.severities[key]} ${SEVERITY_LABELS[key]}`}>
          {row.severities[key]} {SEVERITY_LABELS[key]}
        </Chip>
      ))}
    </ChipList>
  );
}

export function SchwachstellenPage(): ReactNode {
  const [data, setData] = useState<DtInstancesResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [view, setView] = useState<View>('cve');
  const [cves, setCves] = useState<DtCvesResponse | null>(null);
  const [cveLoading, setCveLoading] = useState(false);
  const [cveError, setCveError] = useState<string | null>(null);
  const [cveFilters, setCveFilters] = useState<CveFilters>({ severity: '', onlyKev: false, search: '' });
  const [listNavigation, setListNavigation] = useState(0);

  const [selected, setSelected] = useState<number | null>(null);
  const [instanceSearch, setInstanceSearch] = useState('');
  const [scanFilter, setScanFilter] = useState('');
  const [tab, setTab] = useState<Tab>('befunde');
  const [includeSuppressed, setIncludeSuppressed] = useState(false);
  const [findings, setFindings] = useState<DtFindingsResponse | null>(null);
  const [sbom, setSbom] = useState<DtSbomResponse | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const cveSeq = useRef(0);
  const detailSeq = useRef(0);
  const requestCount = useRef(0);
  const refreshInFlight = useRef(false);
  const instanceDetailRef = useRef<HTMLDivElement>(null);
  const instanceHeadingRef = useRef<HTMLHeadingElement>(null);
  const resultsHeadingRef = useRef<HTMLHeadingElement>(null);


  const showOverviewResults = (target: OverviewTarget): void => {
    // A second click on the marked card removes its filter again: severity
    // and scan status go back to „Alle“, „Instanzen“ back to the CVE view.
    if (isOverviewActive(target)) {
      if (target === 'critical' || target === 'high') setCveFilters({ severity: '', onlyKev: false, search: '' });
      else if (target === 'instances') setView('cve');
      else setScanFilter('');
      setListNavigation((current) => current + 1);
      return;
    }
    // Each card opens its complete category, independent of previous searches.
    setSelected(null);
    setInstanceSearch('');
    setIncludeSuppressed(false);
    if (target === 'critical' || target === 'high') {
      setCveFilters({ severity: target === 'critical' ? 'CRITICAL' : 'HIGH', onlyKev: false, search: '' });
      setView('cve');
    } else {
      setScanFilter(target === 'without-sbom' ? 'kein-projekt' : target === 'unknown' ? 'unbekannt' : '');
      setView('instanz');
    }
    setListNavigation((current) => current + 1);
  };

  const isOverviewActive = (target: OverviewTarget): boolean => {
    if (target === 'critical' || target === 'high') {
      return view === 'cve' && cveFilters.severity === (target === 'critical' ? 'CRITICAL' : 'HIGH') &&
        !cveFilters.onlyKev && !cveFilters.search.trim() && !includeSuppressed;
    }
    return view === 'instanz' && !instanceSearch.trim() &&
      scanFilter === (target === 'without-sbom' ? 'kein-projekt' : target === 'unknown' ? 'unbekannt' : '');
  };

  useEffect(() => {
    if (selected !== null && view === 'instanz') {
      instanceHeadingRef.current?.focus({ preventScroll: true });
      instanceDetailRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [selected, view]);

  const loadInstances = useCallback(async (initial: boolean): Promise<void> => {
    if (initial) setLoading(true);
    requestCount.current += 1;
    try {
      const response = await fetchDtInstances();
      setData(response);
      setLoadError(null);
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      requestCount.current -= 1;
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadInstances(true);
  }, [loadInstances]);

  /* --- CVE view loading -------------------------------------------------- *
   * Fetched lazily and re-fetched when the suppressed toggle flips: the
   * aggregation costs one DT findings call per scanned project, so it is not
   * something to pull while the operator is looking at a single machine.       */

  const loadCves = useCallback(
    async (suppressed: boolean, foreground: boolean): Promise<void> => {
      const seq = ++cveSeq.current;
      if (foreground) setCveLoading(true);
      requestCount.current += 1;
      try {
        const response = await fetchDtCves(suppressed);
        if (seq !== cveSeq.current) return;
        setCves(response);
        setCveError(null);
      } catch (error) {
        if (seq !== cveSeq.current) return;
        setCveError(errorMessage(error));
      } finally {
        requestCount.current -= 1;
        if (foreground && seq === cveSeq.current) setCveLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (view !== 'cve') return;
    void loadCves(includeSuppressed, true);
  }, [includeSuppressed, loadCves, view]);

  /* --- detail loading --------------------------------------------------- */

  const loadDetail = useCallback(
    async (
      instanceId: number,
      activeTab: Tab,
      suppressed: boolean,
      foreground: boolean,
    ): Promise<void> => {
      const seq = ++detailSeq.current;
      if (foreground) setDetailLoading(true);
      setDetailError(null);
      requestCount.current += 1;
      try {
        if (activeTab === 'befunde') {
          const response = await fetchDtFindings(instanceId, suppressed);
          if (seq !== detailSeq.current) return;
          setFindings(response);
        } else {
          const response = await fetchDtSbom(instanceId);
          if (seq !== detailSeq.current) return;
          setSbom(response);
        }
      } catch (error) {
        if (seq !== detailSeq.current) return;
        setDetailError(errorMessage(error));
      } finally {
        requestCount.current -= 1;
        if (foreground && seq === detailSeq.current) setDetailLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (view !== 'instanz') {
      detailSeq.current += 1;
      setDetailLoading(false);
      return;
    }
    if (selected === null) {
      detailSeq.current += 1;
      setFindings(null);
      setSbom(null);
      setDetailLoading(false);
      return;
    }
    void loadDetail(selected, tab, includeSuppressed, true);
  }, [includeSuppressed, loadDetail, selected, tab, view]);

  useEffect(() => {
    if (
      selected !== null &&
      data !== null &&
      !data.rows.some((row) => row.instanz_id === selected)
    ) {
      setSelected(null);
    }
  }, [data, selected]);

  /* --- automatic refresh ------------------------------------------------ *
   * The CVE aggregation walks every scanned project, so hidden tabs and an
   * already-running request are skipped. Returning to the tab refreshes at
   * once; otherwise the visible data is polled every minute without replacing
   * the tables with foreground spinners.                                    */

  const refreshVisible = useCallback(async (): Promise<void> => {
    if (refreshInFlight.current || requestCount.current > 0 || document.hidden) return;
    refreshInFlight.current = true;
    setRefreshing(true);
    try {
      const requests: Promise<void>[] = [loadInstances(false)];
      if (view === 'cve') {
        requests.push(loadCves(includeSuppressed, false));
      } else if (selected !== null) {
        requests.push(loadDetail(selected, tab, includeSuppressed, false));
      }
      await Promise.all(requests);
    } finally {
      refreshInFlight.current = false;
      setRefreshing(false);
    }
  }, [includeSuppressed, loadCves, loadDetail, loadInstances, selected, tab, view]);

  const refreshRef = useRef(refreshVisible);
  useEffect(() => {
    refreshRef.current = refreshVisible;
  });

  useEffect(() => {
    const refreshWhenVisible = (): void => {
      if (!document.hidden) void refreshRef.current();
    };
    const timer = window.setInterval(refreshWhenVisible, AUTO_REFRESH_MS);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    window.addEventListener('focus', refreshWhenVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      window.removeEventListener('focus', refreshWhenVisible);
    };
  }, []);

  /* --- KPIs ------------------------------------------------------------- */

  const counters = useMemo((): { target: OverviewTarget; label: string; value: number; tone: Tone; note: string }[] => {
    const rows = data?.rows ?? [];
    const sum = (key: DtSeverity): number =>
      rows.reduce((total, row) => total + (row.severities[key] ?? 0), 0);
    const withoutProject = rows.filter((row) => row.scan_state === 'kein-projekt').length;
    const unknown = rows.filter((row) => row.scan_state === 'unbekannt').length;
    return [
      { target: 'instances', label: 'Instanzen', value: rows.length, tone: 'neutral', note: `${fmtNumber(rows.filter((row) => row.scan_state === 'ok').length)} mit Scan-Ergebnis` },
      { target: 'critical', label: 'Kritische Befunde', value: sum('CRITICAL'), tone: sum('CRITICAL') > 0 ? 'alert' : 'good', note: 'Über alle Instanzen' },
      { target: 'high', label: 'Hohe Befunde', value: sum('HIGH'), tone: sum('HIGH') > 0 ? 'warn' : 'good', note: 'Über alle Instanzen' },
      {
        target: 'without-sbom',
        label: 'Ohne SBOM',
        value: withoutProject,
        tone: withoutProject > 0 ? 'alert' : 'good',
        note: 'Instanzen ohne Scan-Projekt',
      },
      {
        target: 'unknown',
        label: 'Nicht prüfbar',
        value: unknown,
        tone: unknown > 0 ? 'warn' : 'good',
        note: 'Scan-Status unbekannt',
      },
    ];
  }, [data]);

  const instanceRows = useMemo(() => {
    const term = instanceSearch.trim().toLocaleLowerCase('de-DE');
    return (data?.rows ?? []).filter((row) =>
      (!scanFilter || row.scan_state === scanFilter) &&
      (!term || [row.produkt, row.version, row.kunde, row.seriennummer, row.label]
        .some((value) => value.toLocaleLowerCase('de-DE').includes(term))),
    );
  }, [data, instanceSearch, scanFilter]);

  const columns = useMemo(
    (): readonly DataTableColumn<DtInstanceRow>[] => [
      {
        key: 'kunde',
        header: 'Kunde',
        sortValue: (row) => row.kunde.toLowerCase(),
        render: (row) => <strong>{row.kunde || DASH}</strong>,
      },
      {
        key: 'produkt',
        header: 'Produkt',
        sortValue: (row) => row.produkt.toLowerCase(),
        render: (row) => (
          <>
            <button
              type="button"
              className="vuln-instance-open"
              id={`vuln-instance-${row.instanz_id}`}
              aria-controls={selected === row.instanz_id ? 'vuln-instance-detail' : undefined}
              aria-expanded={selected === row.instanz_id}
              aria-label={`${row.produkt || 'Instanz'} · ${row.seriennummer}: Details ${selected === row.instanz_id ? 'schließen' : 'öffnen'}`}
              onClick={(event) => {
                event.stopPropagation();
                setSelected(selected === row.instanz_id ? null : row.instanz_id);
              }}
            >
              {row.produkt || DASH}<Icon name="chevron-right" size={12} />
            </button>
          </>
        ),
      },
      {
        key: 'sn',
        header: 'Seriennummer',
        width: '14%',
        sortValue: (row) => row.seriennummer,
        render: (row) => <span className="mono vuln-serial" title={row.seriennummer}>{row.seriennummer || DASH}</span>,
      },
      {
        key: 'state',
        header: 'Scan',
        width: '12%',
        sortValue: (row) => row.scan_state,
        render: (row) => (
          <Chip
            tone={
              SCAN_STATE_TONE[row.scan_state] === 'good'
                ? 'good'
                : SCAN_STATE_TONE[row.scan_state] === 'warn'
                  ? 'warn'
                  : SCAN_STATE_TONE[row.scan_state] === 'alert'
                    ? 'alert'
                    : 'idle'
            }
            title={row.note ?? undefined}
          >
            {SCAN_STATE_LABELS[row.scan_state] ?? row.scan_state}
          </Chip>
        ),
      },
      {
        key: 'severities',
        header: 'Befunde',
        width: '18%',
        sortValue: (row) => -(row.severities.CRITICAL ?? 0) * 1000 - (row.severities.HIGH ?? 0),
        render: (row) => <SeverityChips row={row} />,
      },
      {
        key: 'total',
        header: 'Gesamt',
        width: '8%',
        align: 'right',
        sortValue: (row) => row.findings_total,
        render: (row) =>
          row.scan_state === 'ok' ? fmtNumber(row.findings_total) : <span className="muted">{DASH}</span>,
      },
      {
        key: 'risk',
        header: 'Risiko',
        width: '8%',
        align: 'right',
        headerTitle: 'Inherited Risk Score aus Dependency-Track',
        sortValue: (row) => row.risk_score ?? -1,
        render: (row) => fmtScore(row.risk_score),
      },
      {
        key: 'import',
        header: 'SBOM-Import',
        width: '12%',
        sortValue: (row) => String(row.last_bom_import ?? ''),
        render: (row) => <span className="nowrap" title={fmtDate(row.last_bom_import)}>{fmtDay(row.last_bom_import)}</span>,
      },
    ],
    [selected],
  );

  if (loading) return <PageSpinner text="Lädt Dependency-Track-Status…" />;

  const selectedRow = data?.rows.find((row) => row.instanz_id === selected) ?? null;
  const hasCurrentDetail = selected !== null && (tab === 'befunde'
    ? findings?.instanz_id === selected
    : sbom?.instanz_id === selected);

  // Same place and look as the dashboard's „Nach Kunde / Nach Produkt“: the
  // switch sits in the head of whichever list is showing.
  const viewSwitch = (
    <div className="seg view-switch" role="group" aria-label="Ansicht wählen">
      <button type="button" className={view === 'cve' ? 'on' : undefined} aria-pressed={view === 'cve'} onClick={() => setView('cve')}>
        Nach CVE
      </button>
      <button type="button" className={view === 'instanz' ? 'on' : undefined} aria-pressed={view === 'instanz'} onClick={() => setView('instanz')}>
        Nach Instanz
      </button>
    </div>
  );

  return (
    <div className="vuln-page">
      <PageHeader
        title="Schwachstellen"
        actions={
          <span className="vuln-refresh-status" role="status">
            {refreshing ? <Spinner label="Wird aktualisiert…" /> : null}
          </span>
        }
      />

      {loadError ? (
        <Banner kind="error" sub="Prüfen Sie die Verbindung zu CMDBuild und Dependency-Track.">
          {loadError}
        </Banner>
      ) : null}

      {data && !data.dt_configured ? (
        <Banner kind="warn" sub="Dependency-Track unter System → Konfiguration & Status einrichten.">
          Dependency-Track ist nicht konfiguriert. Es können keine Befunde angezeigt werden.
        </Banner>
      ) : null}

      <KpiRow label="Liste nach Kennzahl filtern">
        {counters.map((counter) => (
          <KpiTile
            key={counter.target}
            label={counter.label}
            value={data ? fmtNumber(counter.value) : DASH}
            tone={counter.tone}
            note={counter.note}
            ariaLabel={`${counter.label}: ${data ? fmtNumber(counter.value) : DASH} anzeigen`}
            selected={isOverviewActive(counter.target)}
            controls="vuln-results"
            onClick={() => showOverviewResults(counter.target)}
          />
        ))}
      </KpiRow>

      {view === 'cve' ? (
        <>
        {cveError ? <Banner kind="error">{cveError}</Banner> : null}
        {cves ? <CoverageWarning data={cves} /> : null}
        <section id="vuln-results" className="card vuln-workspace cve-panel" aria-labelledby="vuln-cve-title" aria-busy={cveLoading}>
          <div className="card-head table-head">
            <h2 id="vuln-cve-title" ref={resultsHeadingRef} tabIndex={-1}>Schwachstellen je CVE</h2>
            {cveLoading && cves ? <span className="vuln-inline-loading" role="status"><Spinner label="Befunde werden aktualisiert…" /></span> : null}
            {cves && cves.kev_count > 0 ? <Chip tone="alert">{fmtNumber(cves.kev_count)} aktiv ausgenutzt</Chip> : null}
            {viewSwitch}
          </div>
          {cveLoading && !cves ? (
            <PageSpinner text="Aggregiert Befunde aller Instanzen…" />
          ) : cves ? (
            <CvePanel
              key={listNavigation}
              data={cves}
              filters={cveFilters}
              onFiltersChange={setCveFilters}
              includeSuppressed={includeSuppressed}
              onToggleSuppressed={setIncludeSuppressed}
            />
          ) : null}
        </section>
        </>
      ) : null}

      {view === 'instanz' ? (
      <>
      <section id="vuln-results" className="card vuln-workspace vuln-instances" aria-labelledby="vuln-instances-title">
        <div className="card-head table-head">
          <h2 id="vuln-instances-title" ref={resultsHeadingRef} tabIndex={-1}>Schwachstellen je Instanz</h2>
          {viewSwitch}
        </div>
        <div className="table-toolbar">
          <input id="vuln-instance-search" type="search" value={instanceSearch} onChange={(event) => setInstanceSearch(event.target.value)} aria-label="Instanzen durchsuchen" placeholder="Produkt, Kunde oder Seriennummer suchen…" />
          <select value={scanFilter} onChange={(event) => setScanFilter(event.target.value)} aria-label="Nach Scan-Status filtern">
            <option value="">Alle Scan-Status</option>
            {Object.entries(SCAN_STATE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <div className="table-toolbar-options">
            {instanceSearch || scanFilter ? <button type="button" className="link" onClick={() => { setInstanceSearch(''); setScanFilter(''); }}>Filter zurücksetzen</button> : null}
            <span className="table-count" aria-live="polite">
              {instanceRows.length === (data?.rows.length ?? 0)
                ? `${fmtNumber(instanceRows.length)} ${plural(instanceRows.length, 'Instanz', 'Instanzen')}`
                : `${fmtNumber(instanceRows.length)} von ${fmtNumber(data?.rows.length ?? 0)} Instanzen`}
            </span>
          </div>
        </div>
          {(data?.rows.length ?? 0) === 0 ? (
            <EmptyState title="Keine Produktinstanzen vorhanden">
              Unter „Stammdaten“ anlegen oder per CSV importieren.
            </EmptyState>
          ) : (
            <DataTable
              key={listNavigation}
              rows={instanceRows}
              columns={columns}
              rowKey={(row) => row.instanz_id}
              mobileBreakpoint={960}
              onRowClick={(row) => setSelected(row.instanz_id === selected ? null : row.instanz_id)}
              rowClassName={(row) => row.instanz_id === selected ? 'vuln-instance-selected' : undefined}
              caption="Ausgelieferte Instanzen mit Scan-Status und Befunden"
              pageSize={15}
              resetPageOnRowsChange={false}
              paginationResetKey={`${instanceSearch}|${scanFilter}`}
              emptyText="Keine Instanz passt zu den Filtern."
            />
          )}
      </section>

      {selectedRow ? (
        <div className="card vuln-workspace vuln-instance-detail" ref={instanceDetailRef} id="vuln-instance-detail" role="region" aria-labelledby="vuln-instance-detail-title" aria-busy={detailLoading}>
          <div className="card-head table-head">
            <div className="vuln-detail-title">
              <h2 id="vuln-instance-detail-title" ref={instanceHeadingRef} tabIndex={-1}>{selectedRow.produkt || selectedRow.label}</h2>
              <p>
                {selectedRow.kunde || DASH} · <span className="mono">{selectedRow.seriennummer || DASH}</span>
                {selectedRow.version ? <> · Version <code>{fmtVersion(selectedRow.version, selectedRow.produkt)}</code></> : null}
              </p>
            </div>
            <div className="vuln-detail-actions">
            {detailLoading && hasCurrentDetail ? <span className="vuln-inline-loading" role="status"><Spinner label="Instanzdetails werden aktualisiert…" /></span> : null}
            <div className="seg view-switch" role="group" aria-label="Instanzdetails wählen">
              <button
                type="button"
                className={tab === 'befunde' ? 'on' : undefined}
                aria-pressed={tab === 'befunde'}
                onClick={() => setTab('befunde')}
              >
                Befunde
              </button>
              <button
                type="button"
                className={tab === 'sbom' ? 'on' : undefined}
                aria-pressed={tab === 'sbom'}
                onClick={() => setTab('sbom')}
              >
                SBOM
              </button>
            </div>
            <button type="button" className="vuln-close-detail" aria-label="Instanzdetails schließen" onClick={() => {
              (document.getElementById(`vuln-instance-${selectedRow.instanz_id}`) ?? document.getElementById('vuln-instance-search'))?.focus();
              setSelected(null);
            }}><Icon name="close" /></button>
            </div>
          </div>
          {detailError ? <div className="card-body"><Banner kind="error">{detailError}</Banner></div> : null}

          {detailLoading && !hasCurrentDetail ? (
            <PageSpinner text={tab === 'befunde' ? 'Lädt Befunde…' : 'Lädt SBOM…'} />
          ) : tab === 'befunde' ? (
            findings && findings.instanz_id === selected ? (
              <FindingsPanel
                key={selected}
                data={findings}
                includeSuppressed={includeSuppressed}
                onToggleSuppressed={setIncludeSuppressed}
              />
            ) : null
          ) : sbom && sbom.instanz_id === selected ? (
            <SbomPanel data={sbom} />
          ) : null}

          <p className="vuln-project-note">
            {selectedRow.project_name
              ? `Dependency-Track-Projekt der Version „${selectedRow.project_name}“`
              : 'Kein Dependency-Track-Projekt'}
            {selectedRow.project_uuid ? <> · <code className="mono">{selectedRow.project_uuid}</code></> : null}
          </p>
        </div>
      ) : null}
      </>
      ) : null}
    </div>
  );
}

export default SchwachstellenPage;
